import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  Calendar, 
  Phone, 
  Mail, 
  ArrowRight,
  Play,
  Star,
  Home,
  Clock,
  Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ThankYouPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Check if iframe loads successfully after 3 seconds
    const checkIframeLoad = setTimeout(() => {
      const fallbackDiv = document.getElementById('demo-fallback');
      if (fallbackDiv) {
        // Show fallback if iframe hasn't loaded (due to CSP restrictions)
        fallbackDiv.style.opacity = '1';
        fallbackDiv.style.pointerEvents = 'auto';
      }
    }, 3000);
    
    return () => clearTimeout(checkIframeLoad);
  }, []);

  const handleScheduleConsultation = () => {
    // This would typically integrate with a calendar booking system like Calendly
    // For now, we'll show an enhanced message specific to qualified prospects
    alert('Your Property Management Strategy Session awaits! Our calendar booking system would open here. For immediate scheduling, please call (312) 555-LEGACY or email hello@legacypropertycouncil.com with "Strategy Session" in the subject line. We\'ll send you a calendar link within 2 hours.');
  };

  const goBackHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={goBackHome} className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">Legacy Property Council</span>
            </button>
            
            <Button onClick={goBackHome} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Application Submitted Successfully!
          </h1>
          
          <p className="text-xl text-slate-600 mb-6 max-w-2xl mx-auto">
            Your membership application has been received. Please scroll down to review and digitally sign your Private Member Association contract. 
            Upon signing, your membership and portal access will be activated immediately.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Badge className="bg-gradient-to-r from-green-100 to-green-200 text-green-800 px-4 py-2 text-sm">
              <CheckCircle className="w-4 h-4 mr-2" />
              Application Approved
            </Badge>
            <Badge className="bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-4 py-2 text-sm">
              <Clock className="w-4 h-4 mr-2" />
              Portal activation after signing
            </Badge>
            <Badge className="bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 px-4 py-2 text-sm">
              <Star className="w-4 h-4 mr-2" />
              Immediate membership benefits
            </Badge>
          </div>
        </div>

        {/* Demo Section */}
        <div className="max-w-6xl mx-auto mb-12">
          <Card className="shadow-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-amber-50 text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                  <Play className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl lg:text-3xl">Your Exclusive Platform Demo</CardTitle>
              </div>
              <p className="text-slate-600 text-lg">
                You've earned access to our full interactive demo. Explore real occupancy tracking, rent collection workflows, and maintenance management.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src="https://app.arcade.software/flows/jDfbMHBA2eR82yEIF3I2/embed?embed_type=inline"
                  title="Legacy Property Council Platform Demo"
                  className="absolute top-0 left-0 w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  onError={() => {
                    console.log('Iframe failed to load due to CSP restrictions');
                  }}
                />
                {/* Enhanced fallback for CSP issues */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-amber-50 flex items-center justify-center z-10 opacity-0 transition-all duration-500" id="demo-fallback">
                  <div className="text-center p-8 max-w-md">
                    <div className="relative mb-6">
                      <div className="w-24 h-24 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                        <ArrowRight className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-slate-800 mb-3">Launch Interactive Demo</h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      Experience our real-time property management dashboard with live occupancy tracking, rent collection, and maintenance workflows.
                    </p>
                    
                    <Button 
                      onClick={() => window.open('https://app.arcade.software/flows/jDfbMHBA2eR82yEIF3I2/view', '_blank')}
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      View Live Demo
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    <p className="text-xs text-slate-500 mt-4">Opens in new tab • No registration required</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Highlight */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Real-Time Dashboard</h3>
              <p className="text-slate-600 text-sm">Monitor all properties from one centralized platform</p>
            </CardContent>
          </Card>

          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Star className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Owner Portal</h3>
              <p className="text-slate-600 text-sm">24/7 access to financial reports and property updates</p>
            </CardContent>
          </Card>

          <Card className="text-center group hover:shadow-lg transition-all duration-300">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Automated Workflows</h3>
              <p className="text-slate-600 text-sm">Streamlined rent collection and maintenance requests</p>
            </CardContent>
          </Card>
        </div>

        {/* Demo Engagement Acknowledgment */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Demo Complete</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                You've just experienced what successful property owners use every day to manage their portfolios efficiently. 
                The real-time occupancy tracking, automated rent collection, and streamlined maintenance workflows you saw 
                can be customized for your specific properties and investment goals.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="max-w-3xl mx-auto p-8 shadow-xl bg-gradient-to-r from-blue-50 to-amber-50">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-4">
              Next Steps: Schedule Your Property Management Strategy Session
            </h2>
            <p className="text-slate-600 mb-6 text-lg leading-relaxed">
              You've seen the platform in action. Now let's create a customized property management strategy for your specific portfolio. 
              Our Illinois-licensed experts will analyze your current situation and show you exactly how to maximize your rental income while minimizing your time investment.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="text-left space-y-3">
                <h4 className="font-semibold text-slate-800 text-lg">What We'll Cover:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600">Portfolio analysis & optimization opportunities</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600">Custom service package recommendation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600">ROI projections with our management</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600">Transition timeline & next steps</span>
                  </div>
                </div>
              </div>
              
              <div className="text-left space-y-3">
                <h4 className="font-semibold text-slate-800 text-lg">Session Details:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-600">45-minute consultation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Star className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="text-slate-600">No-pressure, educational focus</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Home className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-600">Virtual or in-person (Chicago area)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-600">Free portfolio assessment included</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <Button 
                onClick={handleScheduleConsultation}
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-12 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Calendar className="w-6 h-6 mr-3" />
                Schedule My Strategy Session
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
              
              <p className="text-sm text-slate-500">
                Prefer to talk first? Call <span className="font-semibold">(312) 555-LEGACY</span> or email hello@legacypropertycouncil.com
              </p>
            </div>
            
            <div className="mt-8 p-4 bg-white/50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-center space-x-4 text-sm text-slate-600">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Illinois Licensed</span>
                </div>
                <div className="hidden sm:block w-1 h-1 bg-slate-400 rounded-full"></div>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-amber-600" />
                  <span>98.5% Collection Rate</span>
                </div>
                <div className="hidden sm:block w-1 h-1 bg-slate-400 rounded-full"></div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;